import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

const CARGO_LABEL = { admin: "Administrador", gestor: "Gestor", operacional: "Operacional", vendedor: "Vendedor" };

const ANONIMO = { id:null, nome:"", email:"", cargo:"operacional", cargoLabel:"Operacional", filiais:[], iniciais:"?" };

// ─── Cache de módulo ────────────────────────────────────────────────
// O perfil do usuário logado é UMA linha que não muda durante a sessão, e
// era buscado uma vez POR INSTÂNCIA do hook — que vive em App, Sidebar e
// Usuarios. Somando os eventos de onAuthStateChange, davam seis consultas
// idênticas por carregamento, 3,3s de rede, disputando as seis conexões que
// o navegador dá por domínio com as consultas de dados (#114).
//
// `emVoo` é o que resolve o caso real: as três instâncias montam no mesmo
// tick, então não basta guardar o resultado — é preciso compartilhar a
// promessa antes de a primeira responder.
let cache  = null;   // { id, dados }
let emVoo  = null;   // { id, promessa }

/** Descarta o cache. Chamar quando o próprio perfil for alterado. */
export function invalidarUsuarioAtual() {
  cache = null;
  emVoo = null;
}

function montar(session, perfil) {
  const email = session.user.email || "";
  // Nome vazio ou igual ao e-mail (dado sujo): usa o prefixo capitalizado.
  let nome = perfil?.nome;
  if (!nome || nome === email) {
    const prefixo = email.split("@")[0];
    nome = prefixo.charAt(0).toUpperCase() + prefixo.slice(1);
  }
  const cargo = perfil?.cargo || "operacional";
  return {
    // O id entra porque a lista de conferências precisa separar o que é meu
    // do que é de outro usuário (#141) — e-mail não serve, muda.
    id: session.user.id,
    nome, email, cargo,
    cargoLabel: CARGO_LABEL[cargo] || "Operacional",
    // Cidades de acesso (#315); vazia = todas. Ver filiaisDoUsuario.
    filiais: perfil?.filiais || [],
    iniciais: nome.trim().charAt(0).toUpperCase() || "?",
  };
}

/**
 * Perfil do usuário da sessão, no máximo uma consulta por sessão.
 *
 * Exportada para o teste conseguir contar as chamadas ao Supabase sem
 * montar componente — o projeto não tem infraestrutura de teste de React.
 */
export async function buscarUsuarioAtual(session) {
  if (!session?.user) { invalidarUsuarioAtual(); return ANONIMO; }
  const id = session.user.id;

  if (cache?.id === id) return cache.dados;
  if (emVoo?.id === id) return emVoo.promessa;

  const promessa = supabase
    // "*" e não a lista de colunas: antes de a migration de filiais (#315)
    // rodar, pedir `filiais` faria a consulta falhar, e todo mundo — o
    // administrador inclusive — cairia no cargo padrão.
    .from("perfis").select("*").eq("id", id).single()
    .then(({ data }) => {
      const dados = montar(session, data);
      cache = { id, dados };
      emVoo = null;
      return dados;
    })
    .catch(e => {
      emVoo = null;
      console.error("perfil do usuário:", e);
      return montar(session, null);
    });

  emVoo = { id, promessa };
  return promessa;
}

export function useUsuarioAtual() {
  const [usuario, setUsuario] = useState(cache?.dados || ANONIMO);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let cancelado = false;

    async function carregar(session) {
      const dados = await buscarUsuarioAtual(session);
      if (!cancelado) { setUsuario(dados); setLoading(false); }
    }

    // Sem catch, uma falha aqui deixava o hook em loading para sempre
    // (nome/cargo do usuário nunca apareciam).
    supabase.auth.getSession()
      .then(({ data }) => carregar(data.session))
      .catch(e => { console.error("getSession (usuário):", e); carregar(null); });

    // ⚠ NÃO chamar supabase.from(...) DENTRO do callback de onAuthStateChange.
    //
    // Era esta a origem do "Aguarde..." infinito no login. O cliente de auth
    // processa os callbacks de forma serializada; carregar() é async e faz
    // await em supabase.from("perfis"), que precisa do token de acesso — ou
    // seja, espera o mesmo processamento que está bloqueado esperando o
    // callback terminar. O signInWithPassword nunca resolvia, o timeout de 8s
    // da tela de Login disparava "O servidor não respondeu", mas a sessão já
    // tinha sido gravada — daí o F5 entrar direto no usuário.
    //
    // setTimeout(…, 0) devolve o controle imediatamente e roda a busca do
    // perfil fora do processamento do evento. É a orientação da própria
    // documentação do supabase-js para callbacks que precisam consultar dados.
    const { data: listener } = supabase.auth.onAuthStateChange((evento, session) => {
      // Trocou de usuário (ou saiu): o cache anterior não vale mais.
      if (evento === "SIGNED_OUT" || (session?.user?.id && cache && cache.id !== session.user.id)) {
        invalidarUsuarioAtual();
      }
      setTimeout(() => { if (!cancelado) carregar(session); }, 0);
    });
    return () => { cancelado = true; listener?.subscription?.unsubscribe(); };
  }, []);

  return { ...usuario, loading };
}
